// Portable SQL pagination values and canonical cache identity shared by apps and hosts.
export type UdsSqlPage = {
  /** Requested page size before the surface and platform safety ceilings. */
  limit: number
  /** Explicit best-effort offset; tied rows may move between requests. */
  offset?: number
  /** Opaque strict-keyset continuation managed by the runtime. */
  cursor?: string
}

export type FrameSqlOptions = {
  page?: UdsSqlPage
  includeTotalCount?: boolean
}

export type NormalizedFrameSqlOptions = {
  page: UdsSqlPage
  includeTotalCount?: boolean
}

export const FRAME_SQL_CACHE_KEY_PREFIX = 'sql:'

/**
 * Pagination-by-default: a bare statement executes as a page of this size.
 * Mirrors the server's DEFAULT_UDS_PAGE_LIMIT; keep the two values identical.
 */
export const DEFAULT_GUEST_PAGE_LIMIT = 1000

/**
 * Canonical transport and recording options; request-local flags never enter
 * cache identity. Every request normalizes to a page: an explicit page passes
 * through and a bare call pages at the default limit, so equivalent calls
 * share one cache entry and one host request.
 */
export const normalizeFrameSqlOptions = (
  options: FrameSqlOptions | undefined,
): NormalizedFrameSqlOptions => {
  const source = options?.page ?? { limit: DEFAULT_GUEST_PAGE_LIMIT }
  const page = {
    limit: source.limit,
    ...(source.offset === undefined ? {} : { offset: source.offset }),
    ...(source.cursor === undefined ? {} : { cursor: source.cursor }),
  }
  return {
    page,
    ...(options?.includeTotalCount === true ? { includeTotalCount: true } : {}),
  }
}

export const frameSqlCacheKey = (sql: string, options?: FrameSqlOptions): string =>
  `${FRAME_SQL_CACHE_KEY_PREFIX}${JSON.stringify({
    s: sql,
    o: normalizeFrameSqlOptions(options),
  })}`
export type PaginatedFrameQueryResult = {
  hasNext: boolean
  /** Next offset, present only when another offset page exists. */
  offset?: number
  /** Next opaque keyset cursor, present only when another keyset page exists. */
  cursor?: string
  /** Exact count up to GraphQL's signed 32-bit Int maximum, when requested. */
  totalCount?: number
}
export type UdsRowLimitParameter = 'maxRows' | 'page.limit'
